import type { ComponentProps } from "react";
import { useEffect, useRef, useState } from "react";
import { tv } from "tailwind-variants";

import { useAutoResizeTextarea } from "@/routes/_private/-hooks/use-auto-resize-textarea";
import { focusRing, IconButton, icons } from "@/ui";

const newMessageComposerVariants = tv({
  slots: {
    icon: "size-5",
  },
});

const { icon } = newMessageComposerVariants();

type NewMessageComposerProps = {
  onSend: (message: string) => void;
  /** Opening draft, read once on mount so later changes never clobber typing. */
  defaultMessage?: string;
  onResize?: (height: number) => void;
} & ComponentProps<"textarea">;

const EMPTY_MESSAGE = "";

export const NewMessageComposer = ({
  defaultMessage,
  disabled,
  onResize,
  onSend,
  ...rest
}: NewMessageComposerProps) => {
  const [message, setMessage] = useState(defaultMessage ?? EMPTY_MESSAGE);
  const textareaRef = useAutoResizeTextarea(message);
  const containerRef = useRef<HTMLDivElement>(null);

  const isEmpty = message.trim().length === 0;

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }
    const resizeObserver = new ResizeObserver(([entry]) => {
      onResize?.(entry.contentRect.height);
    });
    resizeObserver.observe(element);

    return () => {
      return resizeObserver.disconnect();
    };
  }, [onResize]);

  const handleSendMessage = () => {
    if (isEmpty) {
      return;
    }
    onSend(message);
    setMessage(EMPTY_MESSAGE);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="fixed right-0 bottom-0 left-0" ref={containerRef}>
      <div aria-hidden="true" className="h-12 w-full bg-linear-to-t from-canvas to-transparent" />

      <div className="mx-auto flex max-w-4xl flex-col gap-2 bg-canvas px-4 pb-5 lg:px-0">
        <div
          className={`flex items-end gap-2 rounded-3xl border border-line-strong bg-surface p-2 pl-4 transition-[border-color,box-shadow] focus-within:border-focus focus-within:ring-4 focus-within:ring-focus/25 ${focusRing}`}
        >
          <textarea
            {...rest}
            className="max-h-40 min-h-11 flex-1 resize-none self-center overflow-y-auto border-0 bg-transparent p-0 py-2.5 text-base text-content placeholder:text-content-subtle focus:ring-0 focus:outline-none"
            disabled={disabled}
            onChange={(e) => {
              return setMessage(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Message EO Care Team…"
            ref={textareaRef}
            value={message}
            autoFocus
          />

          <IconButton
            className={
              isEmpty
                ? "bg-surface-muted text-content-subtle"
                : "bg-surface-inverted text-content-on-inverted hover:bg-surface-inverted-hover hover:text-content-on-inverted"
            }
            disabled={isEmpty}
            label="Send message"
            onClick={handleSendMessage}
          >
            <icons.PaperAirplaneIcon aria-hidden="true" className={icon()} />
          </IconButton>
        </div>

        <p className="text-center text-sm text-content-muted">{`We'll respond within one business day.`}</p>
      </div>
    </div>
  );
};
